/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    type ReactElement,
    type ReactNode,
    type PropsWithChildren,
    useRef,
    useEffect,
    useCallback,
    useState,
} from 'react';
import { Link } from '@/components/link';
import CartBadge from './cart-badge';
import LoginSheetButton from './login-sheet-button';
import SearchSheet from './search-sheet';
import { useTranslation } from 'react-i18next';
import logo from '/images/logo.svg';
import { Button } from '@/components/ui/button';
import { SparklesIcon } from '@/components/icons';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { launchChat } from '@/components/shopper-agent';
import { validateShopperAgentConfig } from '@/components/shopper-agent/shopper-agent.utils';
import { UITarget } from '@/targets/ui-target';
import { useAnalytics } from '@/hooks/use-analytics';
import { cn } from '@/lib/utils';
import type { AppConfig } from '@/types/config';
import { Component } from '@/lib/decorators/component';
import { RegionDefinition } from '@/lib/decorators';

@Component('header', {
    name: 'Header',
    group: 'Layout',
    description: 'Global site header with navigation, search, and cart',
    embedded: true,
    component_id: 'header',
})
@RegionDefinition([{ id: 'announcement', name: 'Announcement' }])
export class HeaderMetadata {}

interface HeaderProps extends PropsWithChildren {
    beforeHeader?: ReactNode;
    /**
     * Slot rendered above the header's main row. Used for announcement banners
     * or other above-the-fold content that should sit at the top of the page.
     */
    announcementSlot?: ReactNode;
    variant?: 'full' | 'checkout';
    /** Transparent header (with gradient overlay) until scroll — all breakpoints */
    transparentBackground?: boolean;
    /** Plain transparent header until scroll — below lg only (e.g. PDP mobile hero) */
    transparentBackgroundBelowLg?: boolean;
}

export default function Header({
    children,
    beforeHeader,
    announcementSlot,
    variant = 'full',
    transparentBackground = false,
    transparentBackgroundBelowLg = false,
}: HeaderProps): ReactElement {
    const { t } = useTranslation('header');
    const headerRef = useRef<HTMLElement>(null);
    const [hasScrolled, setHasScrolled] = useState(false);
    const config = useConfig<AppConfig>();
    const { trackCommerceAgentEngagement } = useAnalytics();
    const showChat =
        variant === 'full' &&
        (config.commerceAgent?.enabled === 'true' || config.commerceAgent?.enabled === true) &&
        validateShopperAgentConfig(config.commerceAgent);
    const updateHeaderHeight = useCallback(() => {
        if (headerRef.current) {
            const height = `${headerRef.current.offsetHeight}px`;
            headerRef.current.style.setProperty('--header-height', height);
            document.documentElement.style.setProperty('--header-height', height);
        }
    }, []);

    useEffect(() => {
        const el = headerRef.current;
        if (!el) return;
        updateHeaderHeight();
        const observer = new ResizeObserver(updateHeaderHeight);
        observer.observe(el);
        return () => observer.disconnect();
    }, [updateHeaderHeight]);

    useEffect(() => {
        if (!transparentBackground && !transparentBackgroundBelowLg) {
            setHasScrolled(false);
            return;
        }

        const updateScrolledState = () => setHasScrolled(window.scrollY > 0);

        updateScrolledState();
        window.addEventListener('scroll', updateScrolledState, { passive: true });

        return () => window.removeEventListener('scroll', updateScrolledState);
    }, [transparentBackground, transparentBackgroundBelowLg]);

    const isDesktopTransparent = transparentBackground && !hasScrolled;
    const isMobileTransparent = transparentBackgroundBelowLg && !hasScrolled;

    const headerClassName = cn(
        'group/header header-transparent-gradient text-header-foreground sticky top-0 z-50 transition-colors duration-1000',
        (!transparentBackground || hasScrolled) && 'lg:bg-header-background',
        // Keep solid mobile background underneath (same as homepage) — data-mobile-transparent hides it until scroll
        (!transparentBackgroundBelowLg ? !transparentBackground || hasScrolled : true) &&
            'max-lg:bg-header-background'
    );

    if (variant === 'checkout') {
        return (
            <header
                ref={headerRef}
                className={headerClassName}
                data-transparent={isDesktopTransparent || undefined}
                data-mobile-transparent={isMobileTransparent || undefined}>
                <div className="section-container py-2 px-3">
                    <div className="flex items-center h-16">
                        <Link to="/" className="flex-shrink-0 flex items-center" data-testid="header-logo">
                            <img
                                src={logo}
                                alt={t('logoAlt')}
                                className="h-3 lg:h-4 w-auto [filter:var(--header-logo-filter)]"
                            />
                        </Link>
                        <div className="flex-1" />
                        <CartBadge />
                    </div>
                </div>
            </header>
        );
    }

    return (
        <header
            ref={headerRef}
            className={headerClassName}
            data-transparent={isDesktopTransparent || undefined}
            data-mobile-transparent={isMobileTransparent || undefined}>
            {announcementSlot}
            <div className="flex justify-end section-container">{beforeHeader}</div>
            <div className="section-container py-2 px-3">
                {/* Top row: Logo left, Icons right */}
                <div className="grid items-center grid-cols-[1fr_1fr] lg:grid-cols-[1fr_auto_1fr]">
                    {/* Navigation Menu - desktop only, next to logo */}
                    <div className="hidden lg:flex items-center">{children}</div>

                    {/* Logo - color swapped by theme via --header-logo-filter in app.css */}
                    <Link to="/" className="flex-shrink-0 flex items-center p-2" data-testid="header-logo">
                        <img src={logo} alt={t('logoAlt')} className="h-6 w-auto [filter:var(--header-logo-filter)]" />
                    </Link>

                    {/* Icons group - includes mobile hamburger */}
                    <div className="flex items-center justify-end">
                        <UITarget targetId="sfcc.header.before.cart" />
                        {showChat && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="cursor-pointer lg:px-4 px-1 text-header-foreground hover:bg-header-menu-icon-hover-background transition-opacity"
                                onClick={() => {
                                    void trackCommerceAgentEngagement({ surface: 'header' });
                                    launchChat();
                                }}
                                aria-label={t('openChat')}>
                                <SparklesIcon />
                            </Button>
                        )}
                        <LoginSheetButton />
                        <SearchSheet />
                        <CartBadge />
                        <div className="lg:hidden">{children}</div>
                    </div>
                </div>
                <UITarget targetId="sfcc.header.bnpl.banner" />
            </div>
        </header>
    );
}
